<template>
  <div class="min-h-screen bg-white flex flex-col">
    <TrustBar />
    <main class="flex-1 pt-[120px] pb-12">
      <div class="max-w-4xl mx-auto px-4">
        <h1 class="text-3xl font-bold text-gray-900 mb-6">Politique de Cookies</h1>

        <div class="prose prose-gray max-w-none">
          <section class="mb-8">
            <p class="text-gray-600 mb-4">
              <strong>Dernière mise à jour :</strong> Janvier 2025
            </p>
            <p class="text-gray-600 mb-4">
              La présente politique décrit les cookies utilisés par Fluxbuy OÜ sur le site Fluxreload et comment vous pouvez les gérer.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Qu'est-ce qu'un cookie ?</h2>
            <p class="text-gray-600">
              Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, mobile) lorsque vous visitez notre site. Il nous permet de reconnaître votre navigateur et de mémoriser certaines informations pour améliorer votre expérience de navigation.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Types de cookies que nous utilisons</h2>

            <div class="space-y-4">
              <div class="bg-gray-50 rounded-2xl p-6">
                <h3 class="text-xl font-semibold text-gray-900 mb-2">1. Cookies essentiels (strictement nécessaires)</h3>
                <p class="text-gray-600 mb-2">Ces cookies sont indispensables au fonctionnement du site :</p>
                <ul class="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Maintenir votre session lors du processus de paiement</li>
                  <li>Mémoriser vos préférences de pays et de devise (localStorage)</li>
                  <li>Permettre la navigation entre les pages du site</li>
                </ul>
                <p class="text-gray-600 text-sm mt-3">
                  <strong>Note :</strong> Ces cookies ne peuvent pas être désactivés car le site ne fonctionnerait pas correctement sans eux.
                </p>
              </div>

              <div class="bg-gray-50 rounded-2xl p-6">
                <h3 class="text-xl font-semibold text-gray-900 mb-2">2. Cookies de paiement et sécurité</h3>
                <p class="text-gray-600 mb-2">Ces cookies sont utilisés par nos prestataires de paiement sécurisé :</p>
                <ul class="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li><strong>Stripe</strong> : pour sécuriser les transactions par carte bancaire</li>
                  <li><strong>PayPal</strong> : pour permettre les paiements via compte PayPal</li>
                </ul>
                <p class="text-gray-600 text-sm mt-3">
                  Ces cookies sont nécessaires au traitement de vos paiements et sont régis par les politiques de confidentialité de ces prestataires.
                </p>
              </div>

              <div class="bg-gray-50 rounded-2xl p-6">
                <h3 class="text-xl font-semibold text-gray-900 mb-2">3. Cookies de performance et d'analyse</h3>
                <p class="text-gray-600 mb-2">Ces cookies nous aident à comprendre comment vous utilisez le site :</p>
                <ul class="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Analyser les pages visitées et le parcours utilisateur</li>
                  <li>Détecter les erreurs techniques et les problèmes de performance</li>
                  <li>Mesurer l'efficacité de nos campagnes marketing</li>
                </ul>
              </div>

              <div class="bg-gray-50 rounded-2xl p-6">
                <h3 class="text-xl font-semibold text-gray-900 mb-2">4. Cookies de fonctionnalité</h3>
                <p class="text-gray-600 mb-2">Ces cookies mémorisent vos choix pour améliorer votre expérience :</p>
                <ul class="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Votre pays de sélection (détecté automatiquement ou choisi manuellement)</li>
                  <li>Votre devise préférée</li>
                  <li>La langue d'affichage</li>
                </ul>
                <p class="text-gray-600 text-sm mt-3">
                  Ces préférences sont stockées localement sur votre appareil via le localStorage de votre navigateur.
                </p>
              </div>

              <div class="bg-gray-50 rounded-2xl p-6">
                <h3 class="text-xl font-semibold text-gray-900 mb-2">5. Cookies de géolocalisation</h3>
                <p class="text-gray-600 mb-2">Nous utilisons des services tiers pour détecter votre pays :</p>
                <ul class="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li><strong>ipapi.co</strong> : pour détecter automatiquement votre pays et afficher les produits disponibles</li>
                </ul>
                <p class="text-gray-600 text-sm mt-3">
                  Cette information nous permet de vous proposer les produits et tarifs adaptés à votre zone géographique.
                </p>
              </div>
            </div>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Durée de conservation des cookies</h2>
            <p class="text-gray-600 mb-4">La durée de conservation varie selon le type de cookie :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Cookies de session</strong> : supprimés lorsque vous fermez votre navigateur</li>
              <li><strong>Cookies persistants</strong> : conservés pendant plusieurs mois ou années</li>
              <li><strong>LocalStorage</strong> : données stockées sur votre appareil jusqu'à ce que vous les supprimiez</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Gérer vos cookies</h2>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">Via votre navigateur</h3>
            <p class="text-gray-600 mb-4">Vous pouvez configurer votre navigateur pour refuser tous les cookies ou pour supprimer les cookies existants. Notez que cela peut affecter le fonctionnement du site.</p>

            <div class="bg-gray-50 rounded-xl p-4 mb-4">
              <p class="text-gray-700 text-sm font-medium mb-2">Instructions selon votre navigateur :</p>
              <ul class="text-gray-600 text-sm space-y-1">
                <li><strong>Chrome</strong> : Paramètres > Confidentialité et sécurité > Cookies et autres données des sites</li>
                <li><strong>Firefox</strong> : Options > Vie privée et sécurité > Cookies et données de site</li>
                <li><strong>Safari</strong> : Préférences > Confidentialité > Gérer les données des sites web</li>
                <li><strong>Edge</strong> : Paramètres > Cookies et autorisations de site</li>
              </ul>
            </div>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">Cookies tiers</h3>
            <p class="text-gray-600">
              Pour gérer les cookies de nos partenaires (Stripe, PayPal), veuillez consulter leurs politiques de confidentialité respectives :
            </p>
            <ul class="list-disc list-inside text-gray-600 space-y-1 mt-2">
              <li><a href="https://stripe.com/privacy" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Politique de confidentialité Stripe</a></li>
              <li><a href="https://paypal.com/webapps/mpp/ua/privacy-full" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Politique de confidentialité PayPal</a></li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Mises à jour</h2>
            <p class="text-gray-600">
              Cette politique peut être mise à jour pour refléter les changements dans nos pratiques ou pour des raisons réglementaires. Les modifications seront publiées sur cette page avec la nouvelle date de mise à jour.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Contact</h2>
            <p class="text-gray-600">
              Pour toute question concernant l'utilisation des cookies sur notre site, contactez-nous via notre formulaire de contact disponible sur le site.
            </p>
          </section>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from 'vue';
</script>
